import 'extensions/ui-components/x-loader';
import 'extensions/ui-components/x-quantity';
import {defineCustomElement} from 'extensions/ui-utils';
import {html, LitElement, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import stylesheet from './quick-order-input.scss?as=stylesheet';

export class QuickOrderInput extends LitElement {
	static formAssociated = true;

	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			errorMessage: {
				attribute: 'error-message',
				type: String
			},
			index: {
				type: Number
			},
			loading: {
				type: Boolean
			},
			productCode: {
				attribute: 'product-code',
				type: String
			},
			productData: {
				type: Object
			},
			quantity: {
				type: Number
			},
			type: {
				type: String
			}
		};
	}

	get value () {
		const form = new FormData();
		form.append(`Products[ ${this.index + 1} ]:code`, this.productCode);
		form.append(`Products[ ${this.index + 1} ]:quantity`, this.quantity);

		return form;
	}

	get state () {
		return {
			errorMessage: this.errorMessage,
			index: this.index,
			productCode: this.productCode,
			productData: this.productData,
			quantity: this.quantity
		};
	}

	constructor () {
		super();

		/**
		 * @type {string}
		 */
		this.errorMessage = '';

		/**
		 * @type {number}
		 */
		this.index = 0;

		/**
		 * @type {boolean}
		 */
		this.loading = false;

		/**
		 * @type {object}
		 */
		this.productData = {};

		/**
		 * @type {number}
		 */
		this.quantity = 0;

		/**
		 * @type {string}
		 */
		this.productCode = '';

		/**
		 * @type {string}
		 */
		this.type = '';

		this._internals = this.attachInternals();
	}

	updated (changedProperties) {
		if (changedProperties.has('productCode') || changedProperties.has('quantity')) {
			this._internals.setFormValue(this.value);

			const valueMissing = !this.productCode && this.quantity > 0;
			this._internals.setValidity({valueMissing}, valueMissing ? 'Please enter a value.' : '');
		}

		if (changedProperties.has('productCode')) {
			if (this.productCode !== '' && changedProperties.get('productCode') !== this.productCode) {
				this.productFieldSearch(this.productCode);
			}
		}
	}

	renderBackorder () {
		let backorderDateHtml = ``;
		if (this.productData.product._custom.backorder_date > 0 && this.productData.product._custom.backorder_formatted_date) {
			backorderDateHtml = html`<span class="x-product-inventory--backorder-date">ETA: ${this.productData.product._custom.backorder_formatted_date}</span>`;
		}

		return html`
			<div class="u-color-gray-7 t-quick-order-result t-quick-order-result__backorder">
				<div class="u-flex x-product-inventory--backorder">
					<span class="u-icon-backorder t-quick-order-icon x-product-inventory--backorder-icon"></span>
					<div class="t-quick-order-result__text">
						<span class="t-quick-order-result-title">Backordered</span>
						${backorderDateHtml}
					</div>
				</div>
			</div>
		`;
	}

	renderNoStock () {
		return html`
			<div class="u-color-gray-7 t-quick-order-result t-quick-order-result__no-stock">
				<div class="u-flex x-product-inventory--no-stock">
					<span class="u-icon-cross t-quick-order-icon x-product-inventory--no-stock-icon"></span>
					<div class="t-quick-order-result__text">
						<span class="t-quick-order-result-title">No Stock</span>
					</div>
				</div>
			</div>
		`;
	}

	renderReplacement () {
		return html`
			<div class="u-color-gray-7 t-quick-order-result t-quick-order-result__replacement-part">
				<div class="u-flex">
					<span class="u-icon-refresh t-quick-order-icon t-quick-order-icon__refresh"></span>
					<div class="t-quick-order-result__text">
						<span class="t-quick-order-result-title">Replacement Part for:</span>
						<span class="t-quick-order-descrip">${this.productData?.product?.code}</span>
					</div>
				</div>
			</div>
		`;
	}

	renderProductStatus () {
		const resultClasses = {
			't-quick-order-result': true,
			't-quick-order-result__available': this.productData?.product?._custom?.inv_code === '',
			't-quick-order-result__backordered': this.productData?.product?._custom?.inv_code === 'BACKORDERED',
			't-quick-order-result__no-stock': this.productData?.product?._custom?.inv_code === 'NO_STOCK',
			't-quick-order-result__not-purchasable': this.productData?.product?._custom?.inv_code === 'NOT_PURCHASABLE',
			't-quick-order-result__replacement': this.productData?.product?._custom?.inv_code === 'REPLACEMENT_PART'
		};

		const resultIcons = {
			// eslint-disable-next-line array-element-newline
			't-quick-order-icon': true,
			't-quick-order-icon__check u-icon-check': this.productData?.product?._custom?.inv_code !== 'NOT_PURCHASABLE',
			't-quick-order-icon__cross u-icon-cross': this.productData?.product?._custom?.inv_code === 'NOT_PURCHASABLE'
		};

		if (!this.productData?.product) {
			return html`
				<div class="t-quick-order-result t-quick-order-result-error t-quick-order-result__none">
					No Results
				</div>
			`;
		}

		return html`
			<div class="${classMap(resultClasses)}">
				<div class="u-flex">
					<span class="${classMap(resultIcons)}"></span>
					<div class="t-quick-order-result__text">
						${this.renderProductDescription()}
					</div>
				</div>
			</div>
			${this.productData?.product?._custom?.inv_code === 'BACKORDERED' ? this.renderBackorder() : nothing}
			${this.productData?.product?._custom?.inv_code === 'NO_STOCK' ? this.renderNoStock() : nothing}
			${this.productData?.product?._custom?.inv_code === 'REPLACEMENT_PART' ? this.renderReplacement() : nothing}
		`;
	}

	renderProductDescription () {
		if (this.productData?.product?._custom?.inv_code === 'NOT_PURCHASABLE') {
			return html`
				<span class="t-quick-order-result-title">No Longer Available</span>
			`;
		}

		if (this.productData?.product?._custom?.inv_code === 'REPLACEMENT_PART') {
			return html`
				<span class="t-quick-order-result-title">Product:</span>
				<span class="t-quick-order-descrip">${this.productData?.product?.replacement_product?.descrip}</span>
			`;
		}

		return html`
			<span class="t-quick-order-result-title">Product:</span>
			<span class="t-quick-order-descrip">${this.productData?.product?.descrip}</span>
		`;
	}

	renderProductPrice () {
		if (this.productData?.product && this.productData?.product?._custom?.inv_code === 'REPLACEMENT_PART') {
			return html`
				<span class="t-quick-order-result__price u-inline-block">${this.productData?.product.replacement_product.formatted_price}</span>
				${parseFloat(this.productData?.product.replacement_product.base_price) > parseFloat(this.productData?.product.replacement_product.price) ? html`<s class="t-quick-order-result__base-price">${this.productData?.product.replacement_product.formatted_base_price}</s>` : nothing}
			`;
		}

		if (this.productData?.product && this.productData?.product?._custom?.inv_code !== 'NOT_PURCHASABLE') {
			return html`
				<span class="t-quick-order-result__price u-inline-block">${this.productData?.product.formatted_price}</span>
				${parseFloat(this.productData?.product.base_price) > parseFloat(this.productData?.product.price) ? html`<s class="t-quick-order-result__base-price">${this.productData?.product.formatted_base_price}</s>` : nothing}
			`;
		}

		return nothing;
	}

	renderProductDetails () {
		if (!Object.keys(this.productData).length && this.errorMessage === '') {
			return nothing;
		}

		if (this.errorMessage !== '') {
			return html`
				<div class="t-quick-order-result__details">
					<div class="t-quick-order-result t-quick-order-result-error t-quick-order-result__none">
						<div class="u-flex">
							<span class="t-quick-order-icon t-quick-order-icon__cross u-icon-cross"></span>
							<span class="t-quick-order-result-title">${this.errorMessage}</span>
						</div>
					</div>
				</div>
			`;
		}

		return html`
			<div class="t-quick-order-result__details">
				<div class="t-quick-order-result__status">
					${Object.keys(this.productData).length > 0 ? this.renderProductStatus() : nothing}
				</div>
				<div class="t-quick-order-price">
					${this.renderProductPrice()}
				</div>
			</div>
		`;
	}

	render () {
		const classes = {
			't-quick-order-row': true,
			't-quick-order-row--loading': this.loading
		};

		return html`
			<div class="${classMap(classes)}">
				<div class="t-quick-order-input-wrap">
					<div class="t-quick-order-inputs-wrap">
						<label class="u-hide-visually " for="quickorder-product-code">Part #</label>
						<input @blur="${this.handleBlur}" .value="${this.productCode}" class="js-quick-order-input c-form-input c-form-input--large t-quick-order-input" id="quickorder-product-code" name="Product_Code" placeholder="${this.type === 'spareParts' ? 'Enter Part #' : 'Enter Model #'}" type="text" .disabled="${this.loading}">
						<input name="Replaced_Part" type="hidden">
						<button aria-label="Remove" class="t-quick-order-remove-button u-icon-cross u-hidden" type="button"></button>
						<label class="u-hide-visually "	for="quickorder-product-quantity-1">Quantity</label>
						${this.loading ? html`<x-loader class="t-quick-order-loading"></x-loader>` : nothing}
					</div>
					${this.renderProductDetails()}
				</div>
				<div class="t-quick-order-quantity-wrap">
					${this.productData.product && this.productData?.product?._custom?.inv_code !== 'NOT_PURCHASABLE' ? html`<x-quantity class="quantity" name="Quantity" value="${this.quantity}" hide-buttons-mobile hide-buttons-tablet min="0" @change="${this.handleQuantityChange}"></x-quantity>` : html`- -`}
				</div>
			</div>
		`;
	}

	handleBlur (e) {
		this.productCode = e.target.value;
	}

	handleQuantityChange (e) {
		this.quantity = e.target.value;
		this.dispatchEvent(new CustomEvent('stateChange', {
			bubbles: true,
			composed: true,
			detail: this.state
		}));
	}

	async productFieldSearch () {
		this.productData = {};

		if (this.productCode?.trim() === '') {
			return;
		}

		const productData = this.productData?.success === 1 ? this.productData : await this.lookupProduct(this.productCode);
		this.errorMessage = '';

		if (productData && productData.success === 1) {
			this.productData = productData;

			if (productData.product?._custom?.inv_code === 'REPLACEMENT_PART') {
				this.productCode = productData.product?.replacement_product?.code;
			}
		} else {
			this.errorMessage = productData.message || 'An error has occurred. Please try again. ';
			this.productData = {};
			this.quantity = 0;
		}

		this.dispatchEvent(new CustomEvent('stateChange', {
			bubbles: true,
			composed: true,
			detail: this.state
		}));

		this.requestUpdate();
	}

	async lookupProduct (productCode) {
		this.loading = true;
		// Send the request to the options:apiUrl, to get results of a product.
		const response = await fetch(window.mivaJS.API_Url, {
			body: JSON.stringify({
				Function: 'QUICK_ORDER_PRODUCT',
				// eslint-disable-next-line camelcase
				Product_Code: productCode,
				Type: this.type
			}),
			method: 'POST'
		});

		this.loading = false;

		if (!response.ok) {
			throw new Error('Network response was not successful');
		}

		return response.json();
	}
};

defineCustomElement('quick-order-input', QuickOrderInput);
